import React, { useRef, useState } from 'react';
import { useMainPerformanceSalesTrend, useMainPerformanceSalesComparison, SectionProps } from '../../hooks/useMainPerformanceSections';
import LoadingSpinner from '../ui/LoadingSpinner';
import ErrorMessage from '../ui/ErrorMessage';
import EmptyState from '../ui/EmptyState';
import useChartGuide from '../../hooks/useChartGuide';
import { CHART_GUIDES } from '../../constants/chartGuides';
import { useFavoriteToggle } from '../../hooks/useFavoriteToggle';
import SalesTrendHeader from './SalesTrendHeader';
import ComparisonChart from './ComparisonChart';
import NormalizedChart from './NormalizedChart';
import CombinedChart from './CombinedChart';
// import ChartInsights from './ChartInsights';

interface SalesTrendProps extends SectionProps {
  branchId: number;
  timeRange?: string;
}

const SalesTrend: React.FC<SalesTrendProps> = ({
  branchId,
  timeRange = 'last6Months',
  onError,
  showLoading = true,
  compact = false
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const { openGuide } = useChartGuide();
  const { handleFavoriteToggle, isFavorited } = useFavoriteToggle();
  const { data: trendData, isLoading, isError, error, refetch } = useMainPerformanceSalesTrend(branchId, timeRange);
  const { data: comparisonData } = useMainPerformanceSalesComparison(branchId, timeRange);
  
  // Component metadata
  const componentId = 'sales-trend';
  const componentTitle = 'روند فروش';

  const [showNormalizedView, setShowNormalizedView] = useState(true);
  const [showComparisonView, setShowComparisonView] = useState(false);
  const [comparisonInsightType, setComparisonInsightType] = useState<'comparison-invoice' | 'comparison-normalized' | 'comparison-actual'>('comparison-actual');

  // Handle error callback
  React.useEffect(() => {
    if (isError && error && onError) {
      onError(error as Error);
    }
  }, [isError, error, onError]);

  // Helper function to format numbers for display
  const formatKpiValue = (value: number | string, type: 'currency' | 'percentage' | 'number' = 'number'): string => {
    if (typeof value === 'string') return value;
    if (!value && value !== 0) return '0';
    
    switch (type) {
      case 'currency':
        return new Intl.NumberFormat('fa-IR').format(Math.round(value));
      case 'percentage':
        return `${value != null ? value.toFixed(1) : '0'}%`;
      default:
        return new Intl.NumberFormat('fa-IR').format(Math.round(value));
    }
  };

  if (isLoading && showLoading) {
    return (
      <div className="card-base card-padding animate-enter animate-delay-3">
        <div className="flex flex-col items-center justify-center h-64">
          <LoadingSpinner
            variant="minimal"
            size="lg"
            message="در حال بارگذاری روند فروش…"
          />
        </div>
      </div>
    );
  }

  if (isError && error) {
    return (
      <div className="card-base card-padding">
        <ErrorMessage 
          message="خطا در دریافت اطلاعات روند فروش"
          onRetry={() => refetch()}
        />
      </div>
    );
  }

  if (!trendData?.salesTrend) {
    return (
      <div className="card-base card-padding">
        <EmptyState />
      </div>
    );
  }

  const salesTrend = trendData.salesTrend;
  const salesTrendComparison = trendData.salesTrendComparison || [];

  // Extract sales trend data array
  const salesTrendData = Array.isArray(salesTrend) ? salesTrend : (salesTrend?.data || []);
  const currencyInfo = !Array.isArray(salesTrend) ? salesTrend?.currency : null;
  const currencySymbol = currencyInfo?.currency_symbol || 'تومان';

  // Extract sales trend comparison data
  const salesTrendComparisonData = Array.isArray(salesTrendComparison) ? salesTrendComparison : (salesTrendComparison?.data || []);
  const comparisonCurrencyInfo = !Array.isArray(salesTrendComparison) ? salesTrendComparison?.currency : null;
  const comparisonCurrencySymbol = comparisonCurrencyInfo?.currency_symbol || currencySymbol;

  // Get branch and brand sales from the comparison data hook
  const currentBranchSales = comparisonData?.sales?.branch || 0;
  const currentBrandAvgSales = comparisonData?.sales?.brand || 0;

  // Determine which insight type to show
  const getInsightType = () => {
    if (showComparisonView) {
      return comparisonInsightType;
    }
    return 'normalized';
  };

  return (
    <div ref={chartRef} className="card-base card-padding animate-enter animate-delay-3">
      <SalesTrendHeader
        componentId={componentId}
        componentTitle={componentTitle}
        branchId={branchId}
        timeRange={timeRange}
        compact={compact}
        showComparisonView={showComparisonView}
        showNormalizedView={showNormalizedView}
        onOpenGuide={() => openGuide(CHART_GUIDES.salesTrend)}
        onFavoriteToggle={() => handleFavoriteToggle({
          componentId,
          componentTitle,
          dashboardSource: 'mainPerformance',
          componentType: 'chart',
          description: 'روند فروش و تعداد سفارش در طول زمان',
          props: { branchId, timeRange, compact },
          gridLayout: {
            containerClass: 'grid grid-cols-1 gap-6'
          }
        })}
        isFavorited={isFavorited(componentId)}
        chartRef={chartRef}
        onToggleComparisonView={() => {
          setShowComparisonView(!showComparisonView);
          if (!showComparisonView) setShowNormalizedView(false);
        }}
        onToggleNormalizedView={() => setShowNormalizedView(!showNormalizedView)}
      />
      
      {showComparisonView ? (
        <>
          <ComparisonChart
            data={salesTrendComparisonData}
            currencySymbol={comparisonCurrencySymbol}
            formatKpiValue={formatKpiValue}
            onInsightTypeChange={setComparisonInsightType}
          />
          {/* {salesTrendComparisonData.length > 0 && (
            <ChartInsights
              comparisonData={salesTrendComparisonData}
              currencySymbol={comparisonCurrencySymbol}
              formatKpiValue={formatKpiValue}
              insightType={getInsightType()}
            />
          )} */}
        </>
      ) : showNormalizedView ? (
        <>
          <NormalizedChart
            data={salesTrendData}
            currencySymbol={currencySymbol}
            formatKpiValue={formatKpiValue}
          />
          {/* {salesTrendData.length > 0 && (
            <ChartInsights
              salesTrendData={salesTrendData}
              currencySymbol={currencySymbol}
              formatKpiValue={formatKpiValue}
              insightType="normalized"
            />
          )} */}
        </>
      ) : (
        <CombinedChart
          data={salesTrendData}
          currencySymbol={currencySymbol}
          formatKpiValue={formatKpiValue}
        />
      )}
      
      {/* KPI Cards - Branch Monthly Sales and Brand Average */}
      {comparisonData?.sales && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">فروش ماهانه شعبه</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-1">
                  {formatKpiValue(currentBranchSales, 'currency')} {comparisonData?.currency?.currency_symbol || currencySymbol}
                </p>
              </div>
              {/* <div className="text-blue-500">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div> */}
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">میانگین فروش ماهانه برند</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-1">
                  {formatKpiValue(currentBrandAvgSales, 'currency')} {comparisonData?.currency?.currency_symbol || currencySymbol}
                </p>
              </div>
             
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SalesTrend;